import type { Logger } from "@expert/logging";
import type { GaiaBotMessage, ReactiveMessagePayload } from "@expert/shared-types";

import { getSessionType } from "@expert/gaia";
import { trackAppcuesEvent, trackFullStoryEvent } from "@expert/monitoring";
import { processMessage, setDeviceInfo } from "@expert/solve-tools";
import { reactAnalytics } from "@soluto-private/eventualize-react";

import type { ExpertAssistMessage } from "../shared-types";

import { addFailureMessage, addMessage } from "../state";

export const handleIncomingMessage = (msg: GaiaBotMessage, logger: Logger) => {
    const { sessionId, callSid, chatId, formattedMessage, analytics } = parseExpertAssistMessage(msg);
    if (sessionId && formattedMessage) {
        addMessage(sessionId, formattedMessage);
        const reactiveMessage = msg as ReactiveMessagePayload;
        if (reactiveMessage.externalDevices) setDeviceInfo(reactiveMessage.externalDevices, logger);
        void reactAnalytics.dispatcher
            .withIdentities({
                ...(callSid && { CallSid: callSid }),
                ...(sessionId && { SessionId: sessionId }),
                ...(chatId && { ChatId: chatId }),
            })
            .dispatchBusinessEvent("MessageReceived", {
                senderType: "bot",
                messageType: formattedMessage.type,
                messageId: formattedMessage.id,
                ...analytics,
            });
        trackFullStoryEvent("ExpertAssistMessageReceived", {
            sessionId,
            callSid,
            chatId,
            messageType: formattedMessage.type,
            ...analytics,
        });
        trackAppcuesEvent("ExpertAssistMessageReceived", { messageType: formattedMessage.type, ...analytics });
    } else {
        addFailureMessage(sessionId, "botFailure");
        logger.warn(
            { sessionId: sessionId ?? "undefined", hasFormattedMessage: !!formattedMessage, fullMessage: msg },
            "GAIA | Unable to handling incoming message - missing expected value",
        );
    }
};

const parseExpertAssistMessage = (msg: GaiaBotMessage) => {
    let formattedMessage: ExpertAssistMessage | undefined;
    let analytics: Record<string, unknown> | undefined;

    const sessionId = msg.sessionGroupId;

    const sessionType = getSessionType(sessionId);
    const isChat = sessionType === "chat";
    const chatId = isChat ? msg.sessionId : undefined;
    const callSid = sessionType === "voice" && msg.sessionId?.startsWith("C") ? msg.sessionId : undefined; // TODO: This only works for twilio call sids

    if ("response" in msg) {
        const messageId = crypto.randomUUID();
        const { response, analytics: updatedAnalytics } = processMessage({ ...msg, messageId });
        analytics = updatedAnalytics;
        formattedMessage = {
            id: messageId,
            text: response,
            type: "Search",
            isUnread: true,
            status: "active",
            timestamp: msg.responseTimestamp,
        };
    } else if (msg.suggestion.messageType === "Proactive") {
        formattedMessage = {
            id: msg.suggestion.messageId,
            text: msg.suggestion.message,
            type: msg.suggestion.messageType,
            isUnread: true,
            status: "active",
            timestamp: msg.suggestion.outputTimestamp,
            tag: "Solve suggestion",
        };
        analytics = { messageIntent: msg.suggestion.metadata?.intentClassification ?? "unknown" };
    }

    return { formattedMessage, sessionId, callSid, chatId, analytics };
};
